import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Search,
  RefreshCw,
  Users,
  Building,
  CreditCard,
  X,
  Phone,
  Mail,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { beneficiariosAPI } from '../services/api';

const AdminBeneficiarios = () => {
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [contagem, setContagem] = useState({ total: 0, ativos: 0, suspensos: 0, cancelados: 0 });
  const [syncStatus, setSyncStatus] = useState(null);

  // Filtros
  const [filtros, setFiltros] = useState({
    busca: '',
    status: '',
  });
  const [filtrosAtivos, setFiltrosAtivos] = useState([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingBeneficiario, setEditingBeneficiario] = useState(null);
  const [formData, setFormData] = useState({
    nome_completo: '',
    cpf: '',
    numero_matricula: '',
    telefone_principal: '',
    email: '',
    plano_contratado: '',
    empresa_nome: '',
    status: 'ativo',
  });

  // Paginacao
  const [pagina, setPagina] = useState(0);
  const [porPagina] = useState(15);
  const [totalRegistros, setTotalRegistros] = useState(0);

  useEffect(() => {
    loadBeneficiarios();
    loadContagem();
  }, [pagina, filtros.status]);

  useEffect(() => { loadSyncStatus(); }, []);

  useEffect(() => {
    // Atualizar filtros ativos para exibir chips
    const ativos = [];
    if (filtros.status) ativos.push({ tipo: 'status', valor: filtros.status, label: `Status: ${filtros.status}` });
    if (filtros.busca) ativos.push({ tipo: 'busca', valor: filtros.busca, label: `Busca: "${filtros.busca}"` });
    setFiltrosAtivos(ativos);
  }, [filtros]);

  const loadBeneficiarios = async () => {
    setLoading(true);
    try {
      const response = await beneficiariosAPI.listar(pagina * porPagina, porPagina, filtros.status);
      setBeneficiarios(response.data);
      setTotalRegistros(contagem.total || response.data.length);
    } catch (error) {
      toast.error('Erro ao carregar beneficiarios');
    } finally {
      setLoading(false);
    }
  };

  const loadContagem = async () => {
    try {
      const response = await beneficiariosAPI.contagem();
      setContagem(response.data);
      setTotalRegistros(response.data.total);
    } catch (error) {
      console.error('Erro ao carregar contagem');
    }
  };

  const loadSyncStatus = async () => {
    try {
      const { data } = await beneficiariosAPI.statusSync();
      setSyncStatus(data);
    } catch {
      /* aviso e' best-effort: se o proprio check falhar, nao alarma */
    }
  };

  const handleSincronizar = async () => {
    if (!window.confirm('Sincronizar todos os beneficiarios do ERP da operadora?')) return;

    setSincronizando(true);
    try {
      const response = await beneficiariosAPI.sincronizar();
      toast.success(`${response.data.sincronizados} novos, ${response.data.atualizados} atualizados`);
      loadBeneficiarios();
      loadContagem();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar');
    } finally {
      setSincronizando(false);
    }
  };

  // Sync FDW -> esau_beneficiarios (a tabela que a busca le). E este que resolve
  // o "beneficiario recente nao aparece" do aviso de staleness.
  const handleSincronizarEsau = async () => {
    setSincronizando(true);
    try {
      const { data } = await beneficiariosAPI.sincronizarEsau();
      toast.success(`Cadastro atualizado: ${data.total_local ?? ''} beneficiários`);
      loadSyncStatus();
      loadBeneficiarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar cadastro');
    } finally {
      setSincronizando(false);
    }
  };

  const handleBuscar = async () => {
    if (!filtros.busca.trim()) {
      loadBeneficiarios();
      return;
    }
    setLoading(true);
    try {
      const response = await beneficiariosAPI.buscar(filtros.busca, 50);
      setBeneficiarios(response.data);
      setTotalRegistros(response.data.length);
    } catch (error) {
      toast.error('Erro na busca');
    } finally {
      setLoading(false);
    }
  };

  const limparFiltro = (tipo) => {
    if (tipo === 'status') {
      setFiltros({ ...filtros, status: '' });
    } else if (tipo === 'busca') {
      setFiltros({ ...filtros, busca: '' });
      loadBeneficiarios();
    }
  };

  const handleOpenModal = (beneficiario = null) => {
    if (beneficiario) {
      setEditingBeneficiario(beneficiario);
      setFormData({
        nome_completo: beneficiario.nome_completo || '',
        cpf: beneficiario.cpf || '',
        numero_matricula: beneficiario.numero_matricula || '',
        telefone_principal: beneficiario.telefone_principal || '',
        email: beneficiario.email || '',
        plano_contratado: beneficiario.plano_contratado || '',
        empresa_nome: beneficiario.empresa_nome || '',
        status: beneficiario.status || 'ativo',
      });
    } else {
      setEditingBeneficiario(null);
      setFormData({
        nome_completo: '',
        cpf: '',
        numero_matricula: '',
        telefone_principal: '',
        email: '',
        plano_contratado: '',
        empresa_nome: '',
        status: 'ativo',
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.nome_completo || !formData.cpf) {
      toast.error('Nome e CPF sao obrigatorios');
      return;
    }

    try {
      if (editingBeneficiario) {
        await beneficiariosAPI.atualizar(editingBeneficiario.id, formData);
        toast.success('Beneficiario atualizado');
      } else {
        await beneficiariosAPI.criar(formData);
        toast.success('Beneficiario criado');
      }
      setShowModal(false);
      loadBeneficiarios();
      loadContagem();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const maskCPF = (cpf) => {
    if (!cpf || cpf.length !== 11) return cpf;
    return `${cpf.substring(0, 3)}.***.${cpf.substring(6, 9)}-**`;
  };

  const porcentagemAtivos = contagem.total > 0 ? ((contagem.ativos / contagem.total) * 100).toFixed(1) : 0;
  const totalPaginas = Math.ceil(totalRegistros / porPagina);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ color: 'var(--primary)', marginBottom: '0.5rem' }}>
          <Users size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Beneficiarios
        </h2>
        <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem' }}>
          Gerencie os beneficiarios sincronizados do ERP da operadora
        </p>
      </div>

      {/* Aviso de staleness: cadastro nao sincroniza no exato 06:00 (deploy/restart
          pode pular o dia) -> beneficiario recente pode nao aparecer na busca.
          Torna VISIVEL o que antes sumia em silencio. */}
      {syncStatus?.stale && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: '#fff8e1', border: '1px solid #f39c12',
          borderRadius: '10px', padding: '12px 16px', marginBottom: '1.25rem',
          color: '#8a6d00', fontSize: '0.88rem',
        }}>
          <AlertTriangle size={18} color="#f39c12" style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            Cadastro sincronizado {syncStatus.horas_desde_sync != null
              ? `há ${Math.round(syncStatus.horas_desde_sync)}h`
              : 'em data desconhecida'}
            {'. '}Beneficiários incluídos recentemente no ERP podem ainda não
            aparecer na busca. Clique em <strong>Sincronizar</strong> para atualizar.
          </span>
          <button
            onClick={handleSincronizarEsau}
            disabled={sincronizando}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: '#f39c12', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '7px 12px', cursor: 'pointer',
              fontSize: '0.82rem', fontWeight: 700, flexShrink: 0,
            }}
          >
            <RefreshCw size={14} className={sincronizando ? 'spin' : ''} />
            {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        </div>
      )}

      {/* Cards de Estatisticas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="search-box" style={{ padding: '1.25rem', textAlign: 'center', borderLeft: '4px solid var(--primary)' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>
            {contagem.total.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Total</div>
        </div>
        <div className="search-box" style={{ padding: '1.25rem', textAlign: 'center', borderLeft: '4px solid #27ae60' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#27ae60' }}>
            {contagem.ativos.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Ativos ({porcentagemAtivos}%)</div>
        </div>
        <div className="search-box" style={{ padding: '1.25rem', textAlign: 'center', borderLeft: '4px solid #f39c12' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f39c12' }}>
            {(contagem.suspensos || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Suspensos</div>
        </div>
        <div className="search-box" style={{ padding: '1.25rem', textAlign: 'center', borderLeft: '4px solid #e74c3c' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#e74c3c' }}>
            {(contagem.cancelados || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Cancelados</div>
        </div>
      </div>

      {/* Barra de filtros e acoes */}
      <div className="search-box" style={{ padding: '1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Busca */}
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
            <input
              className="form-control"
              style={{ paddingLeft: '36px', minWidth: '250px' }}
              placeholder="Buscar por CPF ou nome..."
              value={filtros.busca}
              onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
            />
          </div>

          {/* Filtro Status */}
          <select
            className="form-control"
            style={{ minWidth: '150px' }}
            value={filtros.status}
            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativos</option>
            <option value="suspenso">Suspensos</option>
            <option value="cancelado">Cancelados</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn btn-secondary"
            onClick={handleSincronizar}
            disabled={sincronizando}
          >
            <RefreshCw size={16} className={sincronizando ? 'spinning' : ''} />
            {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
          </button>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <Plus size={16} />
            Novo
          </button>
        </div>
      </div>

      {/* Chips de filtros ativos */}
      {filtrosAtivos.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {filtrosAtivos.map((f, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'var(--primary)',
                color: 'white',
                padding: '0.35rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
              }}
            >
              {f.label}
              <X
                size={14}
                style={{ cursor: 'pointer' }}
                onClick={() => limparFiltro(f.tipo)}
              />
            </span>
          ))}
        </div>
      )}

      {/* Lista de beneficiarios */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <div className="search-box" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="data-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Beneficiario</th>
                  <th>CPF</th>
                  <th>Plano</th>
                  <th>Empresa</th>
                  <th>Status</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              </thead>
              <tbody>
                {beneficiarios.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
                      Nenhum beneficiario encontrado
                    </td>
                  </tr>
                ) : (
                  beneficiarios.map((b) => (
                    <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => handleOpenModal(b)}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{b.nome_completo}</div>
                        {b.telefone_principal && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                            <Phone size={12} /> {b.telefone_principal}
                          </div>
                        )}
                        {b.email && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Mail size={12} /> {b.email}
                          </div>
                        )}
                      </td>
                      <td>
                        <code style={{ fontSize: '0.85rem', background: 'var(--gray-100)', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                          {maskCPF(b.cpf)}
                        </code>
                      </td>
                      <td>
                        {b.plano_contratado ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <CreditCard size={14} style={{ color: 'var(--primary)' }} />
                            {b.plano_contratado}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--gray-400)' }}>-</span>
                        )}
                      </td>
                      <td>
                        {b.empresa_nome ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Building size={14} style={{ color: 'var(--gray-500)' }} />
                            {b.empresa_nome}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--gray-400)' }}>-</span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            padding: '0.3rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            background:
                              b.status === 'ativo' ? '#e8f5e9' :
                              b.status === 'suspenso' ? '#fff3e0' :
                              '#ffebee',
                            color:
                              b.status === 'ativo' ? '#27ae60' :
                              b.status === 'suspenso' ? '#f39c12' :
                              '#e74c3c',
                          }}
                        >
                          <span style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: 'currentColor',
                          }}></span>
                          {b.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '0.4rem 0.6rem' }}
                          onClick={(e) => { e.stopPropagation(); handleOpenModal(b); }}
                          title="Editar"
                        >
                          <Edit size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginacao */}
          {totalPaginas > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1rem',
              padding: '1rem',
              background: 'white',
              borderRadius: '8px',
            }}>
              <span style={{ color: 'var(--gray-500)', fontSize: '0.9rem' }}>
                Mostrando {pagina * porPagina + 1} - {Math.min((pagina + 1) * porPagina, totalRegistros)} de {totalRegistros.toLocaleString()}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.4rem 0.6rem' }}
                  disabled={pagina === 0}
                  onClick={() => setPagina(0)}
                >
                  <ChevronLeft size={14} />
                  <ChevronLeft size={14} style={{ marginLeft: '-8px' }} />
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.4rem 0.6rem' }}
                  disabled={pagina === 0}
                  onClick={() => setPagina(pagina - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                <span style={{ padding: '0 0.75rem', fontWeight: 500 }}>
                  {pagina + 1} / {totalPaginas}
                </span>

                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.4rem 0.6rem' }}
                  disabled={pagina >= totalPaginas - 1}
                  onClick={() => setPagina(pagina + 1)}
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.4rem 0.6rem' }}
                  disabled={pagina >= totalPaginas - 1}
                  onClick={() => setPagina(totalPaginas - 1)}
                >
                  <ChevronRight size={14} />
                  <ChevronRight size={14} style={{ marginLeft: '-8px' }} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de Edicao/Criacao */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-content" style={{ maxWidth: '550px' }}>
            <div className="modal-header">
              <h3>{editingBeneficiario ? 'Editar Beneficiario' : 'Novo Beneficiario'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Secao: Dados Pessoais */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Dados Pessoais
                </div>
                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.nome_completo}
                    onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                    autoFocus
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>CPF *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.cpf}
                      onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                      maxLength={11}
                      disabled={!!editingBeneficiario}
                      style={editingBeneficiario ? { background: 'var(--gray-100)', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                  <div className="form-group">
                    <label>Matricula</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.numero_matricula}
                      onChange={(e) => setFormData({ ...formData, numero_matricula: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Secao: Contato */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Contato
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Telefone</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.telefone_principal}
                      onChange={(e) => setFormData({ ...formData, telefone_principal: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Secao: Plano */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Plano de Saude
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Plano</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.plano_contratado}
                      onChange={(e) => setFormData({ ...formData, plano_contratado: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Empresa</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.empresa_nome}
                      onChange={(e) => setFormData({ ...formData, empresa_nome: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    className="form-control"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="ativo">Ativo</option>
                    <option value="suspenso">Suspenso</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editingBeneficiario ? 'Salvar Alteracoes' : 'Criar Beneficiario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBeneficiarios;
